import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { spin } from '../../styles/animations';
import Modal from '../common/Modal';
import { useToast } from '../common/Toast';
import {
  MODO_ACTUALIZAR,
  MODO_AGREGAR,
  descargarPlantilla,
  importar,
} from '../../services/importacionService';

// ── Metadatos de entidades (label + prereqs) — se importan directamente ──────
const ENTIDAD_META = {
  marcas: { label: 'Marcas de equipos', prereqs: [] },
  familias: { label: 'Familias de equipos', prereqs: [] },
  tipos: { label: 'Tipos de equipo', prereqs: [] },
  estados: { label: 'Estados de equipo', prereqs: [] },
  sucursales: { label: 'Sucursales', prereqs: [] },
  centroscosto: { label: 'Centros de costo', prereqs: [] },
  ubicaciones: { label: 'Ubicaciones', prereqs: [] },
  motivosdetencion: { label: 'Motivos de detención', prereqs: [] },
  'estados-reparacion': { label: 'Estados de reparación', prereqs: [] },
  tiposcontador: { label: 'Tipos de contador', prereqs: [] },
  'tipos-rutina': { label: 'Tipos de rutina', prereqs: [] },
  sistemas: { label: 'Sistemas', prereqs: [] },
  'familias-repuesto': { label: 'Familias de repuesto', prereqs: [] },
  'marcas-repuesto': { label: 'Marcas de repuesto', prereqs: [] },
  'unidades-medida': { label: 'Unidades de medida', prereqs: [] },
  tiposdocumentacion: { label: 'Tipos de documentación', prereqs: [] },
  mecanicos: { label: 'Mecánicos', prereqs: [] },
  tareasengrase: { label: 'Tareas de engrase', prereqs: [] },
  modelos: { label: 'Modelos', prereqs: ['Marcas de equipos'] },
  obras: { label: 'Obras', prereqs: ['Sucursales', 'Centros de costo'] },
  'sectores-trabajo': { label: 'Sectores de trabajo', prereqs: ['Obras'] },
  componentes: { label: 'Componentes', prereqs: ['Sistemas'] },
  'subfamilias-repuesto': { label: 'Subfamilias de repuesto', prereqs: ['Familias de repuesto'] },
  tareas: { label: 'Tareas', prereqs: ['Sistemas', 'Componentes', 'Depósitos'] },
  repuestos: { label: 'Repuestos', prereqs: ['Marcas de repuesto', 'Familias de repuesto', 'Subfamilias de repuesto', 'Unidades de medida'] },
  'precios-repuestos': { label: 'Precios de repuestos', prereqs: ['Repuestos'] },
  empresas: { label: 'Empresas operativas', prereqs: [] },
  equipos: {
    label: 'Equipos',
    prereqs: ['Marcas de equipos', 'Modelos', 'Familias de equipos', 'Tipos de equipo', 'Estados de equipo', 'Sucursales', 'Ubicaciones', 'Centros de costo', 'Tipos de contador'],
  },
  'catalogo-puntos-control': {
    label: 'Puntos de control',
    prereqs: ['Sistemas','Tareas'],
  },
  'puntos-control': {
    label: 'Check List',
    prereqs: ['Familias de equipos', 'Sistemas', 'Puntos de control'],
  },
  'puntos-control-plantilla': {
    label: 'Puntos de control a la plantilla',
    prereqs: ['Puntos de control (con sistemas asignados)'],
  },
  'planes-mantenimiento': {
    label: 'Planes de mantenimiento',
    prereqs: ['Tipos de contador', 'Marcas de equipos', 'Modelos'],
  },
  'rutinas-mantenimiento': {
    label: 'Rutinas de mantenimiento',
    prereqs: ['Planes de mantenimiento', 'Tipos de rutina', 'Tipos de contador'],
  },
  'rutinas-tareas': {
    label: 'Tareas de rutina de mantenimiento',
    prereqs: ['Rutinas de mantenimiento', 'Tareas'],
  },
  depositos: { label: 'Depósitos', prereqs: ['Sistemas'] },
  'tipos-combustible': { label: 'Tipos de combustible', prereqs: [] },
  'categorias-logistica': { label: 'Categorías de logística', prereqs: [] },
  'categorias-cargas': { label: 'Categorías de cargas', prereqs: [] },
  'centros-logisticos': { label: 'Centros logísticos', prereqs: [] },
  transportistas: { label: 'Transportistas', prereqs: [] },
  'lugares-despacho': { label: 'Lugares de despacho', prereqs: [] },
  'cisternas-despacho': { label: 'Cisternas de despacho', prereqs: ['Lugares de despacho'] },
  'clientes-alquiler': { label: 'Clientes de alquiler', prereqs: [] },
};

// ── Styled components ─────────────────────────────────────────────────────────

const Cuerpo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SeccionTitulo = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const AlertaPrereqs = styled.div`
  background: ${({ theme }) => theme.colors.warningBg};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  color: ${({ theme }) => theme.colors.warningTxt};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};

  i {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const ListaPrereqs = styled.ul`
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
  padding-left: 1.2rem;
`;

const BtnDescargar = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  align-self: flex-start;

  &:hover {
    background: ${({ theme }) => theme.colors.infoHover};
  }
`;

const ZonaUpload = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: 2px dashed ${({ $activo, theme }) => $activo ? theme.colors.primary : theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ $activo, theme }) => $activo ? theme.colors.infoBg : theme.colors.bgSecundario};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.infoBg};
  }

  i {
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ZonaUploadTexto = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

const ArchivoSeleccionado = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.successBg};
  border: 1px solid ${({ theme }) => theme.colors.successBorder};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.successTxt};

  i { flex-shrink: 0; }

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.txtSecondary};
    margin-left: auto;
    padding: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};

    &:hover { color: ${({ theme }) => theme.colors.danger}; }
  }
`;

const BotonesAccion = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  border: 1px solid transparent;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnPrimario = styled(Btn)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-color: ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryHover};
    border-color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const BtnSecundario = styled(Btn)`
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-color: ${({ theme }) => theme.colors.borderDefault};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.hoverRow};
  }
`;

const EstadoCarga = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.borderDefault};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const ResumenCards = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const Card = styled.div`
  flex: 1;
  min-width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ $tipo, theme }) =>
    $tipo === 'exito' ? theme.colors.successBorder :
    $tipo === 'omitido' ? theme.colors.warningBorder :
    theme.colors.dangerBorder};
  background: ${({ $tipo, theme }) =>
    $tipo === 'exito' ? theme.colors.successBg :
    $tipo === 'omitido' ? theme.colors.warningBg :
    theme.colors.dangerBg};

  i {
    font-size: 1.4rem;
    color: ${({ $tipo, theme }) =>
    $tipo === 'exito' ? theme.colors.success :
    $tipo === 'omitido' ? theme.colors.warning :
    theme.colors.danger};
  }
`;

const CardNumero = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ $tipo, theme }) =>
    $tipo === 'exito' ? theme.colors.successTxt :
    $tipo === 'omitido' ? theme.colors.warningTxt :
    theme.colors.dangerTxt};
`;

const CardLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.txtSecondary};
  text-align: center;
`;

const TablaErrores = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ThError = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
`;

const TdError = styled.td`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  vertical-align: top;

  &:first-child {
    color: ${({ theme }) => theme.colors.txtSecondary};
    white-space: nowrap;
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }
`;

const TituloErrores = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.dangerTxt};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const InputFileOculto = styled.input`
  display: none;
`;

const OpcionesModo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const OpcionModo = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ $activo, theme }) => ($activo ? theme.colors.primary : theme.colors.borderDefault)};
  background: ${({ $activo, theme }) => ($activo ? theme.colors.infoBg : theme.colors.bgSecundario)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const OpcionModoTexto = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  }

  span {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.txtSecondary};
  }
`;

const NotaResultado = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

// ── Componente principal ──────────────────────────────────────────────────────

const ESTADO = { INSTRUCCIONES: 'instrucciones', CARGANDO: 'cargando', RESULTADO: 'resultado' };

// Entidades cuya importación no da de alta registros, solo actualiza los que ya
// existen (lista de precios): el selector de modo no aplica.
const ENTIDADES_SOLO_ACTUALIZAN = new Set(['precios-repuestos']);

const ImportarModal = ({ entidad, onClose, onImportado, onDescargarPlantilla, onImportar }) => {
  const meta = ENTIDAD_META[entidad] || { label: entidad, prereqs: [] };
  const eligeModo = !ENTIDADES_SOLO_ACTUALIZAN.has(entidad) && !onImportar;
  const [modo, setModo] = useState(MODO_AGREGAR);
  const [estado, setEstado] = useState(ESTADO.INSTRUCCIONES);
  const [archivo, setArchivo] = useState(null);
  const [arrastrandoSobre, setArrastrandoSobre] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorGeneral, setErrorGeneral] = useState('');
  const inputRef = useRef(null);
  const { toast } = useToast();

  const handleDescargaPlantilla = useCallback(async () => {
    try {
      if (onDescargarPlantilla) {
        await onDescargarPlantilla();
      } else {
        await descargarPlantilla(entidad);
      }
    } catch {
      setErrorGeneral('No se pudo descargar la plantilla. Intentá de nuevo.');
    }
  }, [entidad, onDescargarPlantilla]);

  const handleArchivoElegido = useCallback((file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setErrorGeneral('El archivo debe ser un Excel (.xlsx o .xls).');
      return;
    }
    setErrorGeneral('');
    setArchivo(file);
  }, []);

  const handleInputChange = (e) => handleArchivoElegido(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setArrastrandoSobre(false);
    handleArchivoElegido(e.dataTransfer.files[0]);
  };

  const handleImportar = useCallback(async () => {
    if (!archivo) return;
    setEstado(ESTADO.CARGANDO);
    setErrorGeneral('');
    try {
      const res = onImportar ? await onImportar(archivo) : await importar(entidad, archivo, modo);
      setResultado(res);
      setEstado(ESTADO.RESULTADO);
      const numErrores = res.errores?.length ?? 0;
      const n = res.importados;
      const a = res.actualizados ?? 0;
      const totalOk = n + a;
      if (totalOk > 0 && numErrores === 0) {
        const partes = [];
        if (n > 0) partes.push(`${n} importado${n !== 1 ? 's' : ''}`);
        if (a > 0) partes.push(`${a} actualizado${a !== 1 ? 's' : ''}`);
        toast.success(`Se procesaron ${totalOk} registro${totalOk !== 1 ? 's' : ''}: ${partes.join(' y ')}.`);
      } else if (totalOk > 0) {
        toast.warning(`Se procesaron ${totalOk} registro${totalOk !== 1 ? 's' : ''}. ${numErrores} fila${numErrores !== 1 ? 's' : ''} con errores.`);
      } else if (numErrores === 0 && res.ya_existian > 0) {
        toast.info('Todos los registros de la planilla ya estaban cargados.');
      } else if (numErrores === 0 && res.no_encontrados > 0) {
        toast.warning(`No se encontró ningún registro para actualizar (${res.no_encontrados}).`);
      } else {
        toast.error('No se importó ningún registro. Revisá el detalle de errores.');
      }
      if (totalOk > 0 && onImportado) {
        onImportado();
      }
    } catch (err) {
      // Un corte por tiempo no es un problema del archivo, y decir que no se
      // pudo procesar manda a revisar una planilla que está bien. El servidor
      // aplica la importación entera o ninguna fila, así que reintentar es
      // seguro: no quedan registros cargados a medias de un intento anterior.
      const cortadoPorTiempo = err?.code === 'ECONNABORTED'
        || /timeout/i.test(err?.message || '');
      const msg = err?.response?.data?.detail
        || (cortadoPorTiempo
          ? 'El archivo tardó más de lo esperado y se interrumpió la espera. '
            + 'No se cargó ningún registro. Volvé a intentarlo; si el archivo es muy '
            + 'grande, probá dividirlo en partes.'
          : 'Error al procesar el archivo.');
      setErrorGeneral(msg);
      setEstado(ESTADO.INSTRUCCIONES);
    }
  }, [archivo, entidad, modo, onImportado, onImportar, toast]);

  const handleOtroArchivo = () => {
    setArchivo(null);
    setResultado(null);
    setErrorGeneral('');
    setEstado(ESTADO.INSTRUCCIONES);
  };

  return (
    <Modal
      isOpen
      titulo={`Importar ${meta.label}`}
      onClose={estado !== ESTADO.CARGANDO ? onClose : undefined}
      size="md"
    >
      <Cuerpo>
        {estado === ESTADO.INSTRUCCIONES && (
          <>
            {meta.prereqs.length > 0 && (
              <AlertaPrereqs>
                <i className="fas fa-exclamation-triangle" />
                <div>
                  <strong>Antes de importar {meta.label}</strong>, asegurate de tener cargados:
                  <ListaPrereqs>
                    {meta.prereqs.map((p) => <li key={p}>{p}</li>)}
                  </ListaPrereqs>
                </div>
              </AlertaPrereqs>
            )}

            <div>
              <SeccionTitulo>1. Descargá la plantilla</SeccionTitulo>
              <BtnDescargar type="button" onClick={handleDescargaPlantilla}>
                <i className="fas fa-file-excel" />
                Descargar plantilla Excel
              </BtnDescargar>
            </div>

            {eligeModo && (
              <div>
                <SeccionTitulo>2. Elegí qué hacer con lo que ya está cargado</SeccionTitulo>
                <OpcionesModo>
                  <OpcionModo $activo={modo === MODO_AGREGAR}>
                    <input
                      type="radio"
                      name="modo-importacion"
                      value={MODO_AGREGAR}
                      checked={modo === MODO_AGREGAR}
                      onChange={() => setModo(MODO_AGREGAR)}
                    />
                    <OpcionModoTexto>
                      <strong>Agregar solo lo que falta</strong>
                      <span>
                        Los registros que ya existen no se tocan. Es la opción segura: una
                        planilla desactualizada no puede pisar datos cargados.
                      </span>
                    </OpcionModoTexto>
                  </OpcionModo>
                  <OpcionModo $activo={modo === MODO_ACTUALIZAR}>
                    <input
                      type="radio"
                      name="modo-importacion"
                      value={MODO_ACTUALIZAR}
                      checked={modo === MODO_ACTUALIZAR}
                      onChange={() => setModo(MODO_ACTUALIZAR)}
                    />
                    <OpcionModoTexto>
                      <strong>Actualizar los existentes y agregar los que falten</strong>
                      <span>
                        A los registros que ya existen se les aplican los datos de la planilla.
                        Las celdas vacías nunca borran un dato ya cargado.
                      </span>
                    </OpcionModoTexto>
                  </OpcionModo>
                </OpcionesModo>
              </div>
            )}

            <div>
              <SeccionTitulo>{eligeModo ? '3' : '2'}. Completá y subí el archivo</SeccionTitulo>
              {archivo ? (
                <ArchivoSeleccionado>
                  <i className="fas fa-file-excel" />
                  <span>{archivo.name}</span>
                  <button type="button" onClick={() => { setArchivo(null); if (inputRef.current) inputRef.current.value = ''; }}>
                    <i className="fas fa-times" />
                  </button>
                </ArchivoSeleccionado>
              ) : (
                <ZonaUpload
                  $activo={arrastrandoSobre}
                  onDragOver={(e) => { e.preventDefault(); setArrastrandoSobre(true); }}
                  onDragLeave={() => setArrastrandoSobre(false)}
                  onDrop={handleDrop}
                >
                  <i className="fas fa-cloud-upload-alt" />
                  <ZonaUploadTexto>
                    Arrastrá tu archivo aquí o <strong>hacé clic para elegir</strong>
                  </ZonaUploadTexto>
                  <ZonaUploadTexto>.xlsx · .xls</ZonaUploadTexto>
                  <InputFileOculto
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleInputChange}
                  />
                </ZonaUpload>
              )}
            </div>

            {errorGeneral && (
              <AlertaPrereqs style={{ borderColor: undefined }}>
                <i className="fas fa-exclamation-circle" />
                {errorGeneral}
              </AlertaPrereqs>
            )}

            <BotonesAccion>
              <BtnSecundario type="button" onClick={onClose}>
                Cancelar
              </BtnSecundario>
              <BtnPrimario type="button" onClick={handleImportar} disabled={!archivo}>
                <i className="fas fa-upload" />
                Importar
              </BtnPrimario>
            </BotonesAccion>
          </>
        )}

        {estado === ESTADO.CARGANDO && (
          <EstadoCarga>
            <Spinner />
            <span>Procesando archivo...</span>
          </EstadoCarga>
        )}

        {estado === ESTADO.RESULTADO && resultado && (
          <>
            <ResumenCards>
              <Card $tipo="exito">
                <i className="fas fa-check-circle" />
                <CardNumero $tipo="exito">{resultado.importados}</CardNumero>
                <CardLabel>Importados</CardLabel>
              </Card>
              {resultado.actualizados !== undefined && resultado.modo !== MODO_AGREGAR && (
                <Card $tipo="exito">
                  <i className="fas fa-pen-to-square" />
                  <CardNumero $tipo="exito">{resultado.actualizados}</CardNumero>
                  <CardLabel>Actualizados</CardLabel>
                </Card>
              )}
              <Card $tipo="omitido">
                <i className="fas fa-info-circle" />
                <CardNumero $tipo="omitido">{resultado.ya_existian}</CardNumero>
                <CardLabel>
                  {resultado.modo === MODO_AGREGAR ? 'Ya existían (sin tocar)' : 'Sin cambios'}
                </CardLabel>
              </Card>
              {resultado.no_encontrados !== undefined && (
                <Card $tipo="omitido">
                  <i className="fas fa-question-circle" />
                  <CardNumero $tipo="omitido">{resultado.no_encontrados}</CardNumero>
                  <CardLabel>No encontrados</CardLabel>
                </Card>
              )}
              <Card $tipo="error">
                <i className="fas fa-times-circle" />
                <CardNumero $tipo="error">{resultado.errores?.length ?? 0}</CardNumero>
                <CardLabel>Con errores</CardLabel>
              </Card>
            </ResumenCards>

            <NotaResultado>
              {resultado.modo === MODO_AGREGAR
                ? 'Se dieron de alta solo los registros que faltaban. Los que ya estaban cargados quedaron intactos: para que la planilla también los modifique, volvé a importar eligiendo "Actualizar los existentes".'
                : resultado.modo === MODO_ACTUALIZAR
                  ? 'Los registros que ya estaban se actualizaron con los datos de la planilla; los que aparecen en "Sin cambios" ya coincidían en todo.'
                  : 'Esta planilla solo actualiza registros ya cargados: no da de alta ninguno.'}
            </NotaResultado>

            {resultado.errores?.length > 0 && (
              <div>
                <TituloErrores>
                  <i className="fas fa-exclamation-triangle" />
                  {resultado.errores.length === 1
                    ? '1 fila no se pudo importar'
                    : `${resultado.errores.length} filas no se pudieron importar`}
                  {' '}— el resto sí se procesó
                </TituloErrores>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <TablaErrores>
                    <thead>
                      <tr>
                        <ThError>Fila</ThError>
                        <ThError>Columna</ThError>
                        <ThError>Valor</ThError>
                        <ThError>Problema</ThError>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.errores.map((e, i) => (
                        <tr key={i}>
                          <TdError>{e.fila}</TdError>
                          <TdError>{e.columna}</TdError>
                          <TdError>{e.valor || '—'}</TdError>
                          <TdError>{e.mensaje}</TdError>
                        </tr>
                      ))}
                    </tbody>
                  </TablaErrores>
                </div>
              </div>
            )}

            <BotonesAccion>
              <BtnSecundario type="button" onClick={handleOtroArchivo}>
                <i className="fas fa-redo" />
                Importar otro archivo
              </BtnSecundario>
              <BtnPrimario type="button" onClick={onClose}>
                Cerrar
              </BtnPrimario>
            </BotonesAccion>
          </>
        )}
      </Cuerpo>
    </Modal>
  );
};

export default ImportarModal;
